/**
 * Posts runtime-resolved definitions (content tokens, slot groups, collection
 * bindings) to a parent editor frame so the sidebar can prefill with the same
 * values the rendered page sees — the static contentSchema doesn't capture
 * prop-passed defaults or hook-call filters.
 */

import { useEffect, useRef } from 'react';
import type { CollectionBindingDefinition, ContentTokenDefinition, SlotGroupDefinition } from '../types';

export type RuntimeDefinitionKind = 'token' | 'slotGroup' | 'collectionBinding';

export interface TokenRuntimeDefinition extends Partial<ContentTokenDefinition> {
  blockCatalogEntries?: Array<{ id: string; name: string; description: string; category: string }>;
}

export type SlotGroupRuntimeDefinition = Partial<SlotGroupDefinition>;

export interface CollectionBindingRuntimeDefinition {
  collection?: CollectionBindingDefinition['collection'];
  defaultLimit?: number;
  filter?: Record<string, string>;
}

export interface RuntimeDefinitionByKind {
  token: TokenRuntimeDefinition;
  slotGroup: SlotGroupRuntimeDefinition;
  collectionBinding: CollectionBindingRuntimeDefinition;
}

export interface RuntimeDefinitionMessage<K extends RuntimeDefinitionKind = RuntimeDefinitionKind> {
  type: 'VIBESITE_RUNTIME_DEFINITION';
  payload: {
    kind: K;
    path: string;
    definition: RuntimeDefinitionByKind[K];
  };
}

/**
 * True only when running inside an editor iframe. Evaluated once at module
 * load so deployed sites pay zero cost for the broadcast feature — every
 * call to `reportRuntimeDefinition` and `useReportedRuntimeDefinition`
 * short-circuits here before any work (including JSON.stringify).
 */
export const IS_IN_EDITOR_FRAME =
  typeof window !== 'undefined' && window.parent !== window;

export function reportRuntimeDefinition<K extends RuntimeDefinitionKind>(
  kind: K,
  path: string,
  definition: RuntimeDefinitionByKind[K],
): void {
  if (!IS_IN_EDITOR_FRAME) return;
  window.parent.postMessage(
    { type: 'VIBESITE_RUNTIME_DEFINITION', payload: { kind, path, definition } },
    '*',
  );
}

/**
 * Hook form with ref-based dedupe. `definition` is often an inline literal
 * (unstable identity), so we serialize and compare against the last posted
 * value to avoid flooding the parent frame.
 */
export function useReportedRuntimeDefinition<K extends RuntimeDefinitionKind>(
  kind: K,
  path: string,
  definition: RuntimeDefinitionByKind[K],
  enabled: boolean,
): void {
  const lastKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!IS_IN_EDITOR_FRAME || !enabled) return;
    const key = JSON.stringify(definition);
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    reportRuntimeDefinition(kind, path, definition);
  }, [kind, path, definition, enabled]);
}
